import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Download, Facebook, Link2, Loader2, Trophy, Sparkles, Target, CheckCircle2, Rocket, Zap, Shield, Flame, GraduationCap, Crown, Award } from "lucide-react";
import { toast } from "sonner";
import { webAppUrl, isNativeApp } from "@/lib/platform";

// Base URL for public share pages. On the web this is the current origin;
// inside the Capacitor app it must be `https://prepmaster.ph` (webAppUrl())
// because `window.location.origin` returns `capacitor://localhost` and that
// URL is not reachable by anyone the student wants to share it with.
const SHARE_BASE = () => webAppUrl();

/** Open the native OS share sheet on iOS/Android so the student can pick
 * Facebook, Messenger, WhatsApp, X, Instagram, etc. On web, open the
 * classic Facebook sharer popup instead.
 */
async function shareNativeOrWeb({ url, text, channel = "facebook" }) {
  if (isNativeApp()) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({
        title: "PrepMasters",
        text,
        url,
        dialogTitle: channel === "facebook" ? "Share to Facebook" : "Share",
      });
      return;
    } catch (e) {
      if (String(e?.message || "").toLowerCase().includes("cancel")) return;
    }
    try {
      const { Browser } = await import("@capacitor/browser");
      const web = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
      await Browser.open({ url: web, presentationStyle: "popover" });
      return;
    } catch { /* noop */ }
  }
  if (typeof window !== "undefined") {
    const web = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
    window.open(web, "_blank", "width=600,height=500,noopener,noreferrer");
  }
}

export default function ProgressCard() {
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/student/progress-card").then((r) => setCard(r.data)).catch(() => setCard({}));
  }, []);

  const shareUrl = card?.share_token ? `${SHARE_BASE()}/share/p/${card.share_token}` : null;

  const generateShare = async () => {
    setBusy(true);
    try {
      const r = await api.post("/student/progress-card/share");
      setCard((c) => ({ ...c, ...r.data }));
      toast.success("Share link created");
    } catch {
      toast.error("Could not create share link");
    } finally { setBusy(false); }
  };

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#0F172A" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `prepmasters_progress_${(card.full_name || "card").replace(/\s+/g, "_")}.png`;
      a.click();
      toast.success("Image downloaded");
    } catch {
      toast.error("Could not export image");
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    if (!shareUrl) {
      await generateShare();
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const shareFacebook = async () => {
    let url = shareUrl;
    if (!url) {
      const r = await api.post("/student/progress-card/share");
      setCard((c) => ({ ...c, ...r.data }));
      url = `${SHARE_BASE()}/share/p/${r.data.share_token}`;
    }
    const text = `I've mastered ${card?.mastered_count || 0} questions on PrepMasters! 🚀`;
    await shareNativeOrWeb({ url, text, channel: "facebook" });
  };

  if (!card) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="progress-card-page">
      <button onClick={() => nav(-1)} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> My Progress Card
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Share your journey</h1>
        <p className="text-slate-600 mt-1">Download as image or share with friends to inspire them to prepare smarter.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <ShareCard ref={cardRef} card={card} />

        <Card className="p-5 border-slate-200 space-y-3">
          <h3 className="font-display font-bold">Share options</h3>
          <Button onClick={downloadPng} disabled={busy} className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="download-png-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Download as PNG</>}
          </Button>
          <Button onClick={shareFacebook} variant="outline" className="w-full" data-testid="share-fb-btn">
            <Facebook className="w-4 h-4 mr-2" /> Share on Facebook
          </Button>
          <Button onClick={copyLink} variant="outline" className="w-full" data-testid="copy-link-btn">
            <Link2 className="w-4 h-4 mr-2" /> {shareUrl ? "Copy share link" : "Generate share link"}
          </Button>
          {shareUrl && (
            <div className="text-xs text-slate-500 break-all border border-dashed border-slate-200 rounded p-2 font-mono">{shareUrl}</div>
          )}
          <p className="text-xs text-slate-500 leading-relaxed">
            The share link shows a snapshot of your progress at the time of generation. Re-generate to refresh with your latest stats.
          </p>
        </Card>
      </div>
    </div>
  );
}

// Visual card — exported for reuse on the public share page
import { forwardRef } from "react";
export const ShareCard = forwardRef(function ShareCardInner({ card, footnote }, ref) {
  const initial = (card?.full_name || "?").charAt(0).toUpperCase();
  return (
    <div ref={ref} className="relative overflow-hidden rounded-2xl text-white p-8 sm:p-10 max-w-2xl" data-testid="progress-share-card"
         style={{ background: "linear-gradient(135deg,#0F172A 0%,#1E3A8A 55%,#2563EB 100%)" }}>
      <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-white/5 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-64 h-64 rounded-full bg-indigo-400/10 blur-2xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_smart-prep-board/artifacts/c8fzp7x9_PrepMasters%20New%20Logo%20Night.png"
            alt="PrepMasters"
            crossOrigin="anonymous"
            className="h-7 w-auto"
            data-testid="progress-card-logo"
          />
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/60">Progress Card</span>
        </div>

        <div className="mt-8 flex items-center gap-4">
          <div className="h-14 w-14 rounded-full grid place-items-center bg-white text-[#1E3A8A] font-display font-extrabold text-2xl">
            {initial}
          </div>
          <div className="min-w-0">
            <div className="font-display text-2xl sm:text-3xl font-bold leading-tight truncate">{card?.full_name || "Student"}</div>
            <div className="text-sm text-white/70 truncate">{card?.course_name || "—"}</div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-3">
          <Stat icon={Target} value={card?.accuracy ?? 0} suffix="%" label="Accuracy" />
          <Stat icon={CheckCircle2} value={card?.total_answered ?? 0} label="Answered" />
          <Stat icon={Trophy} value={card?.mastered_count ?? 0} label="Mastered" />
        </div>

        {card?.badges && card.badges.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">Recent Badges</div>
            <div className="flex flex-wrap gap-2">
              {card.badges.slice(0, 5).map((b) => (
                <div key={b.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs font-semibold">
                  <BadgeIcon name={b.icon} />
                  <span>{b.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 flex items-end justify-between gap-4">
          <p className="text-sm text-white/70 leading-snug max-w-xs">
            {(card?.mastered_count || 0) >= 50
              ? "Board-Ready in the making."
              : (card?.accuracy || 0) >= 80
              ? "Sharp Reviewer. Keep that streak."
              : (card?.total_answered || 0) > 0
              ? "On the road to mastery."
              : "Beginning the journey."}
          </p>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">prepmasters.ph</div>
            {footnote && <div className="text-xs text-white/60 mt-1">{footnote}</div>}
          </div>
        </div>
      </div>
    </div>
  );
});

function Stat({ icon: Icon, value, label, suffix = "" }) {
  return (
    <div className="rounded-xl bg-white/10 backdrop-blur border border-white/10 p-4">
      <Icon className="w-4 h-4 text-white/60" strokeWidth={1.75} />
      <div className="mt-2 font-display text-3xl font-bold leading-none">
        {value}<span className="text-base text-white/60">{suffix}</span>
      </div>
      <div className="text-[11px] mt-1 uppercase tracking-[0.18em] text-white/60">{label}</div>
    </div>
  );
}

const SHARE_BADGE_ICONS = {
  rocket: Rocket, zap: Zap, shield: Shield, trophy: Trophy, flame: Flame,
  target: Target, "graduation-cap": GraduationCap, crown: Crown, sparkles: Sparkles,
};
function BadgeIcon({ name }) {
  const Icon = SHARE_BADGE_ICONS[name] || Award;
  return <Icon className="w-3.5 h-3.5" strokeWidth={2} />;
}
